import { useState } from 'react';
import api from '../api/axios';

/**
 * RazorpayButton
 * Drop this anywhere a payment is needed.
 *
 * Props:
 *   paymentId     — Payment record ID from backend
 *   amount        — Display amount (e.g. "₹590")
 *   serviceDesc   — e.g. "ITR Filing #42"
 *   onSuccess     — callback(paymentResponse) after verified
 *   onFailure     — callback(error) optional
 */
export default function RazorpayButton({ paymentId, amount, serviceDesc, onSuccess, onFailure }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const pay = async () => {
    setLoading(true);
    setError('');

    try {
      // 1. Create Razorpay order on backend
      const { data: order } = await api.post(`/api/payment/create-order/${paymentId}`);

      // 2. Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        await loadRazorpayScript();
      }

      // 3. Open Razorpay checkout popup
      const options = {
        key:         order.razorpayKeyId,
        amount:      order.amount,          // paise
        currency:    order.currency,
        name:        'MyTaxNiti',
        description: order.serviceDesc,
        image:       '/favicon.svg',
        order_id:    order.razorpayOrderId,
        prefill: {
          name:  order.userName,
          email: order.userEmail,
          contact: order.userPhone || '',
        },
        theme: { color: '#0B1F3A' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setError('Payment cancelled. You can try again anytime.');
          }
        },

        // 4. On success — verify on backend
        handler: async (response) => {
          try {
            const { data: verified } = await api.post('/api/payment/verify', {
              razorpayOrderId:   response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setLoading(false);
            if (onSuccess) onSuccess(verified);
          } catch (err) {
            setLoading(false);
            const msg = err.response?.data?.error || 'Payment verification failed';
            setError(msg);
            if (onFailure) onFailure(msg);
          }
        },
      };

      // Attach failure handler
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', async (response) => {
        setLoading(false);
        const reason = response.error?.description || 'Payment failed';
        setError(reason);
        // Report failure to backend
        try {
          await api.post(`/api/payment/failure?razorpayOrderId=${order.razorpayOrderId}&reason=${encodeURIComponent(reason)}`);
        } catch { /* silent */ }
        if (onFailure) onFailure(reason);
      });

      rzp.open();

    } catch (err) {
      setLoading(false);
      const msg = err.response?.data?.error || 'Could not initiate payment. Please try again.';
      setError(msg);
      if (onFailure) onFailure(msg);
    }
  };

  return (
    <div>
      {error && (
        <div style={{ background: '#FDEDEC', border: '1px solid #f5b8b8', borderRadius: 8, padding: '.6rem .9rem', fontSize: '.82rem', color: '#C0392B', marginBottom: '.75rem' }}>
          ⚠ {error}
        </div>
      )}
      <button
        onClick={pay}
        disabled={loading}
        style={{
          background: loading ? 'var(--border)' : 'var(--gold)',
          color: loading ? 'var(--muted)' : 'var(--navy)',
          border: 'none',
          borderRadius: 10,
          padding: '.75rem 1.75rem',
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '.92rem',
          fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '.5rem',
          transition: 'all .2s',
        }}>
        {loading
          ? <><span className="spinner" style={{ borderTopColor: 'var(--navy)' }} /> Processing...</>
          : <>💳 Pay {amount}</>
        }
      </button>
    </div>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = resolve;
    script.onerror = () => reject(new Error('Failed to load Razorpay script'));
    document.body.appendChild(script);
  });
}
